import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequests } from '../../shared/api/apiRequests.js';
import { LanguageOptions } from '../../entities/LanguageOptions/index.js';
import { Availability } from '../../entities/Availability/index.js';
import { toast } from 'react-toastify';
import { UploadPicture } from '../../features/UploadPicture/index.js';
import { SEOMetatagsForm } from '../../shared/ui/SEOMetatagsForm';
import { Form, Row, Col } from 'react-bootstrap';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';

const CreateEventPage = () => {
  const [categories, setCategories] = useState([]);
  const [formats, setFormats] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [mainImage, setMainImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '',
    description: '',
    formatId: '',
    timezoneId: '',
    categoryId: 1,
    mainLanguageId: 1,
    availability: {
      availableInAllCountries: true,
      allowedCountriesIds: [],
      disallowedCountriesIds: [],
    },
    eventOrganizer: '',
    eventMembers: '',
    SEOMetatags: {
      Title: null,
      Description: null,
      Keywords: null,
    },
  });

  useEffect(() => {
    const getData = async () => {
      try {
        const [categoriesRes, formatsRes, timezonesRes] = await Promise.all([
          apiRequests.events.getCategories(),
          apiRequests.events.getFormats(),
          apiRequests.events.getTimezones()
        ]);
        setCategories(categoriesRes.items);
        setFormats(formatsRes.items);
        setTimezones(timezonesRes.items);
        setForm((prev) => ({
          ...prev,
          categoryId: categoriesRes.items[0]?.id || 1,
          formatId: formatsRes.items[0]?.id || '',
          timezoneId: timezonesRes.items[0]?.id || ''
        }));
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, []);

  const handleSubmit = () => {
    const body = new FormData();
    body.append('mainImg', mainImage);
    body.append('model', JSON.stringify(form));
    const query = apiRequests.events.create(body);
    toast.promise(query, {
      pending: 'Запрос к серверу',
      error: 'Произошла ошибка',
      success: 'Мероприятие успешно создано',
    });
  };

  if (loading) return <FormLoading />;

  return (
    <FormLayout
      onSubmit={handleSubmit}
      onBack={() => navigate('/events')}
      saving={saving}
      submitText="Создать мероприятие"
      savingText="Создание..."
    >
      <FormSection title="Основная информация">
        <Row>
          <Col lg={8}>
            <FormField label="Название" required>
              <Form.Control
                type="text"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Введите название"
              />
            </FormField>

            <FormField label="Организатор">
              <Form.Control
                type="text"
                value={form.eventOrganizer}
                onChange={(e) => setForm((prev) => ({ ...prev, eventOrganizer: e.target.value }))}
                placeholder="Введите организатора"
              />
            </FormField>

            <FormField label="Описание">
              <Form.Control
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Введите описание"
              />
            </FormField>

            <Row>
              <Col md={4}>
                <FormField label="Категория">
                  <Form.Select
                    value={form.categoryId}
                    onChange={(e) => setForm((prev) => ({ ...prev, categoryId: Number(e.target.value) }))}
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.title}</option>
                    ))}
                  </Form.Select>
                </FormField>
              </Col>
              <Col md={4}>
                <FormField label="Формат">
                  <Form.Select
                    value={form.formatId}
                    onChange={(e) => setForm((prev) => ({ ...prev, formatId: Number(e.target.value) }))}
                  >
                    {formats.map((f) => (
                      <option key={f.id} value={f.id}>{f.title}</option>
                    ))}
                  </Form.Select>
                </FormField>
              </Col>
              <Col md={4}>
                <FormField label="Часовой пояс">
                  <Form.Select
                    value={form.timezoneId}
                    onChange={(e) => setForm((prev) => ({ ...prev, timezoneId: Number(e.target.value) }))}
                  >
                    {timezones.map((tz) => (
                      <option key={tz.id} value={tz.id}>{tz.title}</option>
                    ))}
                  </Form.Select>
                </FormField>
              </Col>
            </Row>
          </Col>

          <Col lg={4}>
            <FormField label="Изображение">
              <UploadPicture onSelect={setMainImage} entityType="event" />
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Настройки">
        <Row>
          <Col lg={6}>
            <LanguageOptions
              onSelect={(val) => setForm((prev) => ({ ...prev, mainLanguageId: val }))}
            />
          </Col>
        </Row>

        <div className="mt-4">
          <Availability
            onSelect={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, availableInAllCountries: val },
              }))
            }
            onSelectAllowed={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, allowedCountriesIds: val },
              }))
            }
            onSelectDisallowed={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, disallowedCountriesIds: val },
              }))
            }
          />
        </div>
      </FormSection>

      <FormSection title="SEO настройки">
        <SEOMetatagsForm
          seoMetatags={form.SEOMetatags}
          onChange={(metaTags) => setForm((prev) => ({ ...prev, SEOMetatags: metaTags }))}
        />
      </FormSection>
    </FormLayout>
  );
};

export default CreateEventPage;
